import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, Clock, CheckCircle, Users } from 'lucide-react';
import { gmudService, aprovadorService } from '../services/api';
import { GMUD, Aprovador } from '../types';
import toast from 'react-hot-toast';
import { formatDateBR } from '../utils/dateUtils';
import GmudIdBadge from '../components/GmudIdBadge';
import JiraChamadoBadge from '../components/JiraChamadoBadge';

const Dashboard: React.FC = () => {
  const [gmuds, setGmuds] = useState<GMUD[]>([]);
  const [aprovadores, setAprovadores] = useState<Aprovador[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [gmudsData, aprovadoresData] = await Promise.all([
          gmudService.list(),
          aprovadorService.list(),
        ]);
        setGmuds(gmudsData);
        setAprovadores(aprovadoresData);
      } catch (error) {
        toast.error('Erro ao carregar dados do dashboard');
        console.error('Erro:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStatusCount = (status: string) => {
    return gmuds.filter(gmud => gmud.status === status).length;
  };

  const getImpactoRiscoCount = (impacto: string) => {
    return gmuds.filter(gmud => gmud.impacto_risco === impacto).length;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'concluido':
        return 'text-success-600 bg-success-100';
      case 'em_execucao':
        return 'text-warning-600 bg-warning-100';
      case 'correcao_solicitada':
        return 'text-orange-700 bg-orange-100';
      case 'aprovado':
        return 'text-primary-600 bg-primary-100';
      case 'pendente':
        return 'text-warning-600 bg-warning-100';
      case 'cancelado':
        return 'text-danger-600 bg-danger-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getImpactoColor = (impacto: string) => {
    switch (impacto) {
      case 'critico':
        return 'text-danger-600 bg-danger-100';
      case 'alto':
        return 'text-warning-600 bg-warning-100';
      case 'medio':
        return 'text-primary-600 bg-primary-100';
      case 'baixo':
        return 'text-success-600 bg-success-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const recentGMUDs = gmuds.slice(0, 5);

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>Dashboard</h1>
          <p className='mt-2 text-gray-600'>Visão geral do sistema de gestão de GMUDs</p>
        </div>
        <div className='mt-4 sm:mt-0'>
          <Link to='/gmuds/novo' className='btn-primary inline-flex items-center gap-2'>
            <Plus className='h-5 w-5' />
            Novo GMUD
          </Link>
        </div>
      </div>

      {/* Cards de estatísticas */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
        <div className='card'>
          <div className='flex items-center'>
            <div className='flex-shrink-0'>
              <FileText className='h-8 w-8 text-primary-600' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-500'>Total de GMUDs</p>
              <p className='text-2xl font-semibold text-gray-900'>{gmuds.length}</p>
            </div>
          </div>
        </div>

        <div className='card'>
          <div className='flex items-center'>
            <div className='flex-shrink-0'>
              <Users className='h-8 w-8 text-success-600' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-500'>Aprovadores</p>
              <p className='text-2xl font-semibold text-gray-900'>{aprovadores.length}</p>
            </div>
          </div>
        </div>

        <div className='card'>
          <div className='flex items-center'>
            <div className='flex-shrink-0'>
              <Clock className='h-8 w-8 text-warning-600' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-500'>Pendentes</p>
              <p className='text-2xl font-semibold text-gray-900'>{getStatusCount('pendente')}</p>
            </div>
          </div>
        </div>

        <div className='card'>
          <div className='flex items-center'>
            <div className='flex-shrink-0'>
              <CheckCircle className='h-8 w-8 text-success-600' />
            </div>
            <div className='ml-4'>
              <p className='text-sm font-medium text-gray-500'>Concluídos</p>
              <p className='text-2xl font-semibold text-gray-900'>{getStatusCount('concluido')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos de status e impacto */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Status dos GMUDs */}
        <div className='card'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>Status dos GMUDs</h3>
          <div className='space-y-3'>
            {[
              'rascunho',
              'pendente',
              'aprovado',
              'em_execucao',
              'correcao_solicitada',
              'concluido',
              'cancelado',
            ].map(status => {
                const count = getStatusCount(status);
                const percentage = gmuds.length > 0 ? (count / gmuds.length) * 100 : 0;
                const statusLabels = {
                  rascunho: 'Rascunho',
                  pendente: 'Pendente',
                  aprovado: 'Aprovado',
                  em_execucao: 'Em Execução',
                  correcao_solicitada: 'Correção solicitada',
                  concluido: 'Concluído',
                  cancelado: 'Cancelado',
                };

                return (
                  <div key={status} className='flex items-center justify-between'>
                    <div className='flex items-center'>
                      <span className={`badge ${getStatusColor(status)} mr-3`}>
                        {statusLabels[status as keyof typeof statusLabels]}
                      </span>
                      <span className='text-sm text-gray-600'>{count}</span>
                    </div>
                    <div className='flex-1 mx-4'>
                      <div className='w-full bg-gray-200 rounded-full h-2'>
                        <div
                          className={`h-2 rounded-full ${
                            status === 'concluido'
                              ? 'bg-success-500'
                              : status === 'em_execucao'
                              ? 'bg-warning-500'
                              : status === 'correcao_solicitada'
                              ? 'bg-orange-500'
                              : status === 'aprovado'
                              ? 'bg-primary-500'
                              : status === 'pendente'
                              ? 'bg-warning-500'
                              : status === 'cancelado'
                              ? 'bg-danger-500'
                              : 'bg-gray-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <span className='text-sm text-gray-500 w-12 text-right'>
                      {percentage.toFixed(0)}%
                    </span>
                  </div>
                );
              }
            )}
          </div>
        </div>

        {/* Impacto/Risco */}
        <div className='card'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>Impacto/Risco</h3>
          <div className='space-y-3'>
            {['critico', 'alto', 'medio', 'baixo'].map(impacto => {
              const count = getImpactoRiscoCount(impacto);
              const percentage = gmuds.length > 0 ? (count / gmuds.length) * 100 : 0;
              const impactoLabels = {
                critico: 'Crítico',
                alto: 'Alto',
                medio: 'Médio',
                baixo: 'Baixo',
              };

              return (
                <div key={impacto} className='flex items-center justify-between'>
                  <div className='flex items-center'>
                    <span className={`badge ${getImpactoColor(impacto)} mr-3`}>
                      {impactoLabels[impacto as keyof typeof impactoLabels]}
                    </span>
                    <span className='text-sm text-gray-600'>{count}</span>
                  </div>
                  <div className='flex-1 mx-4'>
                    <div className='w-full bg-gray-200 rounded-full h-2'>
                      <div
                        className={`h-2 rounded-full ${
                          impacto === 'critico'
                            ? 'bg-danger-500'
                            : impacto === 'alto'
                            ? 'bg-warning-500'
                            : impacto === 'medio'
                            ? 'bg-primary-500'
                            : 'bg-success-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                  <span className='text-sm text-gray-500 w-12 text-right'>
                    {percentage.toFixed(0)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* GMUDs recentes */}
      <div className='card'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-gray-900'>GMUDs Recentes</h3>
          <Link to='/gmuds' className='text-primary-600 hover:text-primary-700 text-sm font-medium'>
            Ver todos →
          </Link>
        </div>

        {recentGMUDs.length === 0 ? (
          <p className='text-gray-500 text-center py-8'>Nenhum GMUD encontrado</p>
        ) : (
          <div className='overflow-hidden'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    ID
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Título
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Responsável
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Status
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Data Prevista
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {recentGMUDs.map(gmud => (
                  <tr key={gmud.id} className='hover:bg-gray-50'>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='flex flex-col items-start gap-1'>
                        <GmudIdBadge id={gmud.id} />
                        <JiraChamadoBadge chamado={gmud.jira_chamado} />
                      </div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <Link
                        to={`/gmuds/${gmud.id}`}
                        className='text-sm font-medium text-primary-600 hover:text-primary-700'
                      >
                        {gmud.titulo}
                      </Link>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {gmud.responsavel}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span className={`badge ${getStatusColor(gmud.status)}`}>
                        {gmud.status === 'rascunho'
                          ? 'Rascunho'
                          : gmud.status === 'pendente'
                          ? 'Pendente'
                          : gmud.status === 'aprovado'
                          ? 'Aprovado'
                          : gmud.status === 'em_execucao'
                          ? 'Em Execução'
                          : gmud.status === 'correcao_solicitada'
                          ? 'Correção solicitada'
                          : gmud.status === 'concluido'
                          ? 'Concluído'
                          : 'Cancelado'}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {formatDateBR(gmud.data_prevista)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
